import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { CalendarDays, X, Download, Copy, Twitter, TrendingUp } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";

const SITE_URL = typeof window !== "undefined" ? window.location.origin : "";
const fmtXp = (x) => (x >= 1_000_000 ? `${(x / 1_000_000).toFixed(1)}M` : x >= 1_000 ? `${Math.round(x / 1_000)}K` : `${x || 0}`);

export default function WeeklyRecap({ profile }) {
  const profileId = profile?.id;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [cardUrl, setCardUrl] = useState(null);

  const shareUrl = info ? `${API}/u/${encodeURIComponent(info.rsn)}` : SITE_URL;
  const caption = info
    ? `My week on TaskScape (${info.week_label}) as ${info.rsn}: +${fmtXp(info.xp_gain)} XP, +${info.level_gain} levels, ${info.tasks_done} tasks done (+${info.points} pts)! 🔥 Track your OSRS grind & compete for a free Bond 🏆`
    : "";

  const loadCard = useCallback(async () => {
    try {
      const res = await api.get(`/profiles/${profileId}/weekly-recap-card.png`, { responseType: "blob" });
      setCardUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(res.data);
      });
    } catch {
      /* card is best-effort */
    }
  }, [profileId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/profiles/${profileId}/weekly-recap`);
      setInfo(data);
      await loadCard();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }, [profileId, loadCard]);

  const openModal = () => {
    setOpen(true);
    load();
  };

  // Auto-pop once per ISO week (Monday onward) for the previous Mon–Sun window.
  // Re-checks on tab focus + a slow interval so long-lived sessions still get it.
  const checkAuto = useCallback(async () => {
    if (!profileId || document.hidden) return;
    try {
      const { data } = await api.get(`/profiles/${profileId}/weekly-recap`);
      const seenKey = `ts_recap_seen_${profileId}`;
      if (localStorage.getItem(seenKey) === data.week_key) return;
      localStorage.setItem(seenKey, data.week_key);
      if ((data.tasks_done || 0) > 0 || (data.xp_gain || 0) > 0 || (data.level_gain || 0) > 0) {
        setInfo(data);
        setOpen(true);
        loadCard();
      }
    } catch {
      /* silent — auto-pop is a nicety */
    }
  }, [profileId, loadCard]);

  useEffect(() => {
    if (!profileId) return;
    setInfo(null);
    const t = setTimeout(checkAuto, 1200);
    const onVis = () => { if (!document.hidden) checkAuto(); };
    document.addEventListener("visibilitychange", onVis);
    const iv = setInterval(checkAuto, 30 * 60 * 1000); // re-check every 30 min
    return () => {
      clearTimeout(t);
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [profileId, checkAuto]);

  const close = () => {
    setOpen(false);
    if (cardUrl) URL.revokeObjectURL(cardUrl);
    setCardUrl(null);
  };

  const download = () => {
    if (!cardUrl) return;
    const a = document.createElement("a");
    a.href = cardUrl;
    a.download = `taskscape-week-${(info?.rsn || "recap").replace(/\s+/g, "_")}.png`;
    a.click();
  };

  const shareX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyForDiscord = async () => {
    try {
      await navigator.clipboard.writeText(`${caption}\n${shareUrl}`);
      toast.success("Recap copied! Paste it in Discord to flex your week.");
    } catch {
      toast.error("Couldn't copy — long-press the caption to copy it manually.");
    }
  };

  const Stat = ({ label, value, color, testid }) => (
    <div className="text-center px-2" data-testid={testid}>
      <p className="osrs-shadow text-[10px] text-[#E6A519] uppercase">{label}</p>
      <p className="osrs-num osrs-shadow text-2xl" style={{ color }}>{value}</p>
    </div>
  );

  return (
    <>
      <button data-testid="weekly-recap-btn" onClick={openModal} className="osrs-btn px-3 py-2 text-xs flex items-center gap-2">
        <CalendarDays size={14} /> Weekly recap
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 px-4" onClick={close}>
          <div className="osrs-panel p-5 w-full max-w-xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="weekly-recap-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="osrs-title text-2xl flex items-center gap-2"><TrendingUp size={20} /> Weekly Recap</h3>
              <button onClick={close} className="osrs-btn px-2 py-1" data-testid="close-recap"><X size={16} /></button>
            </div>
            <p className="osrs-shadow text-[#E6A519] text-xs mb-4" data-testid="recap-week-label">
              {info ? `${info.rsn} · ${info.week_label}` : "Loading your week..."}
            </p>

            {loading || !info ? (
              <p className="osrs-shadow text-[#E6A519] text-center py-16" data-testid="recap-loading">Tallying your week...</p>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-2 border-2 border-black bg-[#241b10] py-3 mb-3" data-testid="recap-stats">
                  <Stat label="XP" value={`+${fmtXp(info.xp_gain)}`} color="#00FF00" testid="recap-xp" />
                  <Stat label="Levels" value={`+${info.level_gain}`} color="#FFCF3F" testid="recap-levels" />
                  <Stat label="Tasks" value={info.tasks_done} color="#00FFFF" testid="recap-tasks" />
                  <Stat label="Points" value={`+${info.points}`} color="#FF7A1A" testid="recap-points" />
                </div>
                <p className="osrs-shadow text-[#b9a06a] text-xs text-center mb-4" data-testid="recap-breakdown">
                  {info.quests} quests · {info.diaries} diaries · {info.bosses} boss tasks · {info.skilling} skilling
                </p>

                <div className="border-2 border-black bg-black/40 mb-4 overflow-hidden">
                  {cardUrl ? (
                    <img src={cardUrl} alt="Your weekly recap card" className="w-full block" data-testid="recap-card-img" />
                  ) : (
                    <p className="osrs-shadow text-[#E6A519] text-center py-10 text-xs">Forging your recap card...</p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 justify-center">
                  <button data-testid="recap-download-btn" onClick={download} disabled={!cardUrl} className="osrs-btn px-4 py-2 text-sm flex items-center gap-2">
                    <Download size={15} /> Download
                  </button>
                  <button data-testid="recap-x-btn" onClick={shareX} className="osrs-btn px-4 py-2 text-sm flex items-center gap-2">
                    <Twitter size={15} /> Share on X
                  </button>
                  <button data-testid="recap-discord-btn" onClick={copyForDiscord} className="osrs-btn px-4 py-2 text-sm flex items-center gap-2">
                    <Copy size={15} /> Copy for Discord
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
